import { MethodType, ParamsFetchType, GenerateUrlApiType, ResponseType } from 'src_common/browser/apiUtils';
import qs from 'query-string';
import { GenerateUrlApiParamsType } from 'src_common/server/webDriver/sdkApiWebUtils';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';

const TagsIO = t.interface({
    freebetCredits: t.union([t.array(t.unknown), t.undefined, t.null]),
    accountAdjustment: t.union([t.null, t.undefined, t.boolean]),
    adjustmentType: t.union([t.null, t.undefined, t.string]),
    tradingAdjustment: t.union([t.null, t.undefined, t.boolean]),
});

const PaymentMethodTypeIO = t.union([
    t.literal('blockchain'),
    t.literal('credit-card'),
    t.literal('bank-transfer'),
    t.literal('cash'),
    t.literal('cheque'),
    t.literal('manual'),
    t.literal('casino-bonus'),
    t.literal('open-banking'),
]);
export type PaymentMethodType = t.TypeOf<typeof PaymentMethodTypeIO>;

const PaymentMethodIO = t.union([
    t.interface({
        id: t.string,
        expires: t.union([t.string, t.undefined, t.null]),
        lastUsedAt: t.string,
        name: t.string,
        number: t.string,
        provider: t.string,
        providerReference: t.string,
        subtype: t.string,
        type: PaymentMethodTypeIO,
    }),
    t.interface({
        id: t.union([t.string, t.undefined, t.null]),
        expires: t.union([t.string, t.undefined, t.null]),
        lastUsedAt: t.union([t.string, t.undefined, t.null]),
        name: t.union([t.string, t.undefined, t.null]),
        number: t.union([t.string, t.undefined, t.null]),
        provider: t.string,
        providerReference: t.union([t.string, t.undefined, t.null]),
        subtype: t.union([t.string, t.undefined, t.null]),
        type: PaymentMethodTypeIO,
    }),
    t.undefined,
    t.null,
]);

type PaymentMethodResponseType = t.TypeOf<typeof PaymentMethodIO>;
const isPaymentMethod = createGuard(PaymentMethodIO);

const PaymentResponseIO = t.union([
    t.interface({
        providerOrderId: t.union([t.null, t.undefined, t.string]),
        providerAuthCode: t.union([t.null, t.undefined, t.string]),
    }),
    t.undefined,
    t.null,
]);

const GameNameResponseIO = t.union([t.null, t.undefined, t.string]);

type PaymentResponseType = t.TypeOf<typeof PaymentResponseIO>;
const isPaymentResponseType = createGuard(PaymentResponseIO);

type GameNameResponseType = t.TypeOf<typeof GameNameResponseIO>;
const isGameNameResponseType = createGuard(GameNameResponseIO);

const RabSelectionIO = t.interface({
    market: t.interface({ name: t.string }),
    selection: t.union([t.interface({ name: t.string }), t.undefined, t.null]),
});

const LegIO = t.interface({
    id: t.string,
    competition: t.union([t.null, t.undefined, t.interface({ name: t.string })]),
    event: t.interface({ name: t.string }),
    market: t.union([t.interface({ name: t.string }), t.undefined, t.null]),
    price: t.union([t.null, t.interface({ f: t.string })]),
    priceType: t.string,
    result: t.union([
        t.null,
        t.undefined,
        t.interface({
            type: t.string,
            placeReduction: t.union([t.null, t.undefined, t.interface({ den: t.number, num: t.number })]),
            r4Deductions: t.union([t.null, t.undefined, t.string, t.number]),
        }),
    ]),
    selection: t.union([t.interface({ name: t.string }), t.undefined, t.null]),
    selections: t.union([t.array(RabSelectionIO), t.undefined, t.null]),
    type: t.string,
});

type LegDataType = t.TypeOf<typeof LegIO>;
const isLegDataType = createGuard(LegIO);

const PlacedByIO = t.interface({
    name: t.string,
    type: t.string,
});

const TransactionDetailsIO = t.interface({
    paymentMethod: t.union([t.null, t.undefined, t.unknown]),
    paymentResponse: t.union([t.null, t.undefined, t.unknown]),
    description: t.union([t.null, t.undefined, t.string]),
    gameName: t.union([t.null, t.undefined, t.string]),
    placedBy: t.union([t.null, t.undefined, PlacedByIO]),
    displayedReason: t.union([t.null, t.undefined, t.string]),
    comment: t.union([t.null, t.undefined, t.string]),
    legs: t.union([t.unknown, t.undefined, t.null]),
    channel: t.union([t.null, t.undefined, t.string]),
    type: t.union([t.null, t.undefined, t.string]),
    eachWay: t.union([t.null, t.undefined, t.boolean]),
    cashOut: t.union([t.null, t.undefined, t.boolean]),
});

type TransactionDetailsDataType = t.TypeOf<typeof TransactionDetailsIO> & {
    legs: Array<LegDataType>;
    paymentMethod: PaymentMethodResponseType;
    paymentResponse: PaymentResponseType;
    gameName: string | null | undefined;
};
const isTransactionDetailsDataType = createGuard(TransactionDetailsIO);

const TransactionDataTypeIO = t.interface({
    id: t.number,
    status: t.string,
    currency: t.string,
    details: t.unknown,
    tags: TagsIO,
    finalizedAt: t.union([t.null, t.undefined, t.string]),
    fundsLocked: t.number,
    totalAmount: t.union([t.null, t.undefined, t.number]),
    type: t.string,
});

type TransactionDataType = t.TypeOf<typeof TransactionDataTypeIO> & {
    details: TransactionDetailsDataType;
};
const isTransactionDataType = createGuard(TransactionDataTypeIO);

const AssetFlowsDataItemTypeIO = t.interface({
    balanceAfter: t.union([t.null, t.undefined, t.number]),
    balanceDelta: t.number,
    currency: t.string,
    date: t.string,
    id: t.number,
    tax: t.number,
    transaction: t.unknown,
    type: t.string,
});

export type AssetFlowsDataItemType = t.TypeOf<typeof AssetFlowsDataItemTypeIO> & {
    transaction: TransactionDataType;
};
const isAssetFlowsDataItemType = createGuard(AssetFlowsDataItemTypeIO);

const AssetFlowsResponseIO = t.interface({
    current: t.number,
    elements: t.unknown,
    elementsCount: t.number,
    pageSize: t.number,
});

export type AssetFlowsResponseDataType = t.TypeOf<typeof AssetFlowsResponseIO> & {
    elements: Array<AssetFlowsDataItemType>;
};
const isAssetFlowsResponseType = createGuard(AssetFlowsResponseIO);

const isArray = createGuard(t.array(t.unknown));
const isArrayOrNull = createGuard(t.union([t.array(t.unknown), t.null, t.undefined]));

const decodeLegsModel = (legsModel: unknown): Array<LegDataType> => {
    if (!isArrayOrNull(legsModel)) {
        console.error('Expected array of legs');
        return [];
    }
    const out: Array<LegDataType> = [];
    if (legsModel !== null && legsModel !== undefined) {
        for (const leg of legsModel) {
            if (isLegDataType(leg)) {
                out.push(leg);
            } else {
                console.error('expected leg', leg);
            }
        }
    }

    return out;
};

const decodePaymentMethodModel = (paymentMethod: unknown): PaymentMethodResponseType => {
    if (!isPaymentMethod(paymentMethod)) {
        console.error('expected PaymentMethod', paymentMethod);
        return {
            id: '',
            type: 'credit-card',
            expires: '',
            lastUsedAt: '',
            name: '',
            number: '',
            provider: '',
            providerReference: '',
            subtype: '',
        };
    }
    return paymentMethod;
};

const decodePaymentResponseModel = (paymentResponse: unknown): PaymentResponseType => {
    if (!isPaymentResponseType(paymentResponse)) {
        console.error('expected PaymentResponseType', paymentResponse);
        return {
            providerOrderId: null,
            providerAuthCode: null,
        };
    }
    return paymentResponse;
};

const decodeGameNameModel = (gameName: unknown): GameNameResponseType => {
    if (!isGameNameResponseType(gameName)) {
        console.error('expected PaymentResponseType', gameName);
        return null;
    }
    return gameName;
};

const decodeModel = (model: unknown): AssetFlowsDataItemType | null => {
    if (!isAssetFlowsDataItemType(model)) {
        console.error('expected model AssetFlowsDataItem', model);
        return null;
    }

    const transaction = model.transaction;
    if (!isTransactionDataType(transaction)) {
        console.error('expected model TransactionData', transaction);
        return null;
    }

    const transactionDetails = transaction.details;
    if (!isTransactionDetailsDataType(transactionDetails)) {
        console.error('expected model TransactionDetails', transactionDetails);
        return null;
    }

    const { legs, paymentMethod, paymentResponse, gameName } = transactionDetails;

    const legsModel = decodeLegsModel(legs);

    const paymentMethodModel = decodePaymentMethodModel(paymentMethod);
    const paymentResponsedModel = decodePaymentResponseModel(paymentResponse);
    const paymentGameName = decodeGameNameModel(gameName);

    return {
        ...model,
        transaction: {
            ...transaction,
            details: {
                ...transactionDetails,
                legs: legsModel,
                paymentMethod: paymentMethodModel,
                paymentResponse: paymentResponsedModel,
                gameName: paymentGameName,
            },
        },
    };
};

const decode = (status: number, data: ResponseType): AssetFlowsResponseDataType => {
    if (status === 200 && data.type === 'json') {
        if (!isAssetFlowsResponseType(data.json)) {
            throw Error('Expected AssetFlowsResponseType model');
        }
        const model = data.json;
        const elementsArr = model.elements;

        if (!isArray(elementsArr)) {
            throw Error('Expected array of elements');
        }

        const out: Array<AssetFlowsDataItemType> = [];
        for (const element of elementsArr) {
            const decodedModel = decodeModel(element);
            if (decodedModel !== null) {
                out.push(decodedModel);
            }
        }

        return {
            ...model,
            elements: out,
        };
    }
    throw Error(`getAssetFlows -> Unsupported response code=${status}`);
};

export interface AssetFlowsParamsType {
    userId: number;
    page: number;
    perPage: number;
    startDate: string;
    endDate: string;
    type?: string;
    status?: string;
    operationKind?: 'incoming' | 'outgoing';
}

export const getAssetFlows = {
    browser: {
        params: (params: AssetFlowsParamsType): ParamsFetchType<AssetFlowsParamsType> => {
            return {
                type: MethodType.POST,
                url: '/api-web/asset-flows',
                body: params,
            };
        },
        decode,
    },
    express: {
        method: MethodType.POST,
        urlBrowser: '/api-web/asset-flows',
    },
    generateUrlApi: async (params: GenerateUrlApiParamsType<AssetFlowsParamsType>): Promise<GenerateUrlApiType> => {
        if (params.userSessionId === null) {
            return {
                passToBackend: false,
                status: 403,
                responseBody: {
                    errorMessage: 'User id invalid',
                },
            };
        }

        const { page, perPage, userId, type, status, operationKind, startDate, endDate } = params.req.body;

        const balanceDeltaSign = operationKind === 'incoming' ? 1 : operationKind === 'outgoing' ? -1 : undefined;
        const transactionType = type === undefined ? undefined : type.split(';');

        const query = {
            sort: '-date',
            'transaction.type': transactionType,
            'transaction.status': status,
            balanceDeltaSign,
            startDate,
            endDate,
            perPage,
            page,
        };

        const queryParams = qs.stringify(query);
        const finalUrl = `${params.API_URL}/asset-flows/${params.API_UNIVERSE}/${userId}?${queryParams}`;

        return {
            url: finalUrl,
            passToBackend: true,
            method: MethodType.GET,
        };
    },
};
